import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { headerManager } from "@/lib/requests";
import { api } from "@/lib/openapi";
import { useToast } from "primevue/usetoast";
import { baseSuccessToastMessage } from "@/primevue/init";

import { defineStore, storeToRefs } from "pinia";
import { useOrganizationManager } from "./organization-manager";
import { Stek, useSteksManager, useSteksStore } from "@/stores/steks-store";

export const useStekStore = defineStore("stek", () => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { organization } = useOrganizationManager();
    const { steks, stekNames, isLoading: isLoadingSteks } = useSteksManager();
    const { getSteks } = useSteksStore();

    const stek = ref<Stek | undefined>();

    const showSelectStek = ref(false);

    async function selectStekFromSteks() {
        if (!steks.value) return;
        if (!route.meta.requiresStek) {
            stek.value = undefined;
            return;
        }

        // When there is no selected organization, there is no way to
        // select the current stek.
        if (!route.params.orgName) {
            showSelectStek.value = false;
            return;
        }

        const stekName = route.params.stekName;
        if (stek.value && stek.value.name === stekName) {
            return;
        }

        // If there are no SuperSteks redirect to the creation page, unless
        // we're currently loading the steks.
        if (steks.value.length === 0) {
            router.push(`/${route.params.orgName}/stek/create`);
            stek.value = undefined;
            return;
        }

        if (steks.value.length === 1) {
            stek.value = steks.value[0];
        } else {
            stek.value = steks.value.find((stek) => stek.name === stekName);
        }
        if (!stek.value) {
            showSelectStek.value = true;
            return;
        }

        // When stekName is undefined while a stek is selected, update
        // the url to include the selected stek. (this can happen when there
        // is only one stek, in which case the user won't be shown the stek
        // picker where clicking a stek updates the url.)
        if (!stekName && organization.value && route.meta.requiresStek) {
            router.push(`/${organization.value.name}/steks/${stek.value.name}`);
        }

        showSelectStek.value = false;
    }

    /**
     * Deletes the current Stek.
     */
    async function remove(opts: { showToast: boolean } = { showToast: true }) {
        if (!stek.value) {
            console.warn("Tried to delete stek, but no stek active.");
            return;
        }
        if (!organization.value) {
            console.warn("Tried to delete stek, but no organization active.");
            return;
        }

        const { response } = await api.startStekStekMgtDeleteStekStartPost({});

        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Stek ${stek.value.name} deleted`,
                });
            }
            await getSteks();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete " + stek.value.name,
                    detail: response.status,
                });
            }
        }
    }

    watch(
        () => route.meta.requiresStek,
        (newRequiresStek) => {
            if (!newRequiresStek) {
                stek.value = undefined;
            } else {
                selectStekFromSteks();
            }
        },
        { immediate: true }
    );

    // When the list of steks changes, try to select a stek.
    watch(
        steks,
        (newSteks) => {
            if (!newSteks?.length) {
                stek.value = undefined;
                return;
            }
            selectStekFromSteks();
        },
        { deep: true, immediate: true }
    );

    // When the stekName in the pathname changes, try to select the new
    // stek using the new stekName.
    watch(
        () => route.params.stekName,
        (newValue, oldValue) => {
            const selectedOrganization = organization.value;
            if (!selectedOrganization) {
                return;
            }

            if (newValue !== oldValue) {
                selectStekFromSteks();
            }
        },
        { immediate: true }
    );

    // Set the header for the WebComponents requests lib.
    watch(
        () => stek.value?.identity,
        (newIdentity) => {
            if (newIdentity) {
                headerManager.updateHeaders({
                    stek: newIdentity,
                });
            }
        },
        { immediate: true }
    );

    return { showSelectStek, stekNames, isLoadingSteks, stek, remove };
});

export const useStekManager = () => storeToRefs(useStekStore());
